import React, { Fragment, useMemo, useState } from 'react';
import PropTypes from 'prop-types';

import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';

import EditIcon from '@mui/icons-material/EditOutlined';
import MoreIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';

import Table from '../shared/table';

import './teams.scss';

const TeamsTable = ({
  teams = [],
  onEditClick = () => {},
  onDeleteClick = () => {},
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalRows,
  order,
  setOrder,
  orderBy,
  setOrderBy,
  procedures,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);

  const columns = useMemo(() => {
    return [
      {
        title: 'Name',
        field: 'name',
        type: 'custom',
        sort: true,
        formatter: (value) => <span style={{ fontWeight: 'bold' }}>{value}</span>
      },
      { title: 'Description', field: 'description', type: 'default', sort: true },
      {
        title: 'Location',
        field: 'countries',
        type: 'custom',
        sort: true,
        formatter: (value) => (
          value?.length ? value?.map((country) => (
            <Chip
              key={country}
              size="small"
              label={country}
              variant="outlined"
              sx={{ fontWeight: 500, backgroundColor: 'var(--color-main-light)', borderColor: 'var(--color-main-border)', mr: 1 }}
            />
          )) : null
        ),
      },
      {
        title: 'Procedures',
        field: 'procedures',
        type: 'custom',
        formatter: (value, row) => {
          const visible = value.slice(0, 2);
          const remaining = value.length - 2;

          if (!!row?.allProcedures) {
            return (
              <Chip
                size="small"
                label="All Procedures"
                variant="outlined"
                sx={{
                  fontWeight: 500,
                  backgroundColor: 'var(--color-main-light)',
                  borderColor: 'var(--color-main-border)',
                }}
              />
            )
          }

          if (!value?.length) return null;

          return (
            <>
              {visible.map((id) => (
                <Chip
                  key={id}
                  size="small"
                  label={procedures?.find((p) => p.id === id)?.name}
                  variant="outlined"
                  sx={{
                    fontWeight: 500,
                    backgroundColor: 'var(--color-main-light)',
                    borderColor: 'var(--color-main-border)',
                    mr: 1,
                  }}
                />
              ))}

              {remaining > 0 && (
                <Chip
                  size="small"
                  label={`+${remaining} more`}
                  variant="outlined"
                  sx={{
                    fontWeight: 500,
                    backgroundColor: 'var(--color-main-light)',
                    borderColor: 'var(--color-main-border)',
                  }}
                />
              )}
            </>
          );
        },
      },
      {
        title: 'Members',
        field: 'users',
        type: 'custom',
        formatter: (value) => (
          <Chip
            size="small"
            label={`${value?.length} member${value?.length !== 1 ? 's' : ''}`}
            variant="outlined"
            sx={{ fontWeight: 500, backgroundColor: 'var(--color-main-light)', borderColor: 'var(--color-main-border)', mr: 1 }}
          />
        ),
      },
      {
        title: '',
        field: 'id',
        type: 'custom',
        filter: false,
        width: 50,
        formatter: (value, row) => (
          <>
            <IconButton
              size="small"
              onClick={handleMenuOpen}
              aria-controls={`${row.id}_actions`}
              aria-haspopup="true"
              aria-label="actions"
              color="inherit"
              id={`${row.id}_actions`}
            >
              <MoreIcon color="secondary" />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              elevation={1}
              id={`${row.id}_actions`}
              keepMounted
              onClose={handleMenuClose}
              open={Boolean(anchorEl) && anchorEl.id === `${row.id}_actions`}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
            >
              <MenuItem
                onClick={() => {
                  onEditClick(row);
                  handleMenuClose();
                }}
              >
                <EditIcon fontSize="small" style={{ marginRight: 8 }} color="secondary" />
                Edit Team
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  onDeleteClick(row?.id);
                  handleMenuClose();
                }}
              >
                <DeleteIcon fontSize="small" style={{ marginRight: 8, fill: '#F04438' }} />
                <Typography sx={{ color: '#F04438' }}>Delete Team</Typography>
              </MenuItem>
            </Menu>
          </>
        ),
      },
    ];
  }, [teams, procedures, anchorEl]);

  return (
    <Fragment>
      <Table
        stickyTable
        rows={teams?.map((team) => ({
          ...team,
          country: team?.country || '',
        }))}
        columns={columns}
        page={page}
        setPage={setPage}
        rowsPerPage={rowsPerPage}
        setRowsPerPage={setRowsPerPage}
        totalRows={totalRows}
        order={order}
        setOrder={setOrder}
        orderBy={orderBy}
        setOrderBy={setOrderBy}
      />
    </Fragment>
  );
};

TeamsTable.propTypes = {
  teams: PropTypes.array,
  distributors: PropTypes.array,
  onVerifyClick: PropTypes.func,
  onSMSClick: PropTypes.func
};

export default TeamsTable;
